import { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";

import api from "../../services/api";
import { usePagedList } from "../../hook/usePagedList";
import { useSearchInput } from "../../hook/useSearchInput";
import { useClient } from "../../hook/useClient";
import { toTree } from "../Groups/GroupsPanel";
import Pager from "../../components/ui/Pager";
import SeenPill, { unseen } from "../../components/ui/SeenPill";
import NewThreadModal from "./NewThreadModal";
import { relativeTime, exactTime } from "../../utils/time";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

/**
 * Every discussion of this client system.
 *
 * Filtered and paged on the server, which also scopes the list to the
 * caller's own system -- there is no client filter here because there is
 * nothing to choose between. What is left to narrow by is the group and
 * whether a discussion is hidden. Search runs as you type (2026-10-22).
 */
const ThreadsPage = () => {
  const { clientId, canManage } = useClient();
  const [scope, setScope] = useState({ group: "" });
  const [groups, setGroups] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get("/groups/", { params: { client: clientId } })
      .then((res) => live && setGroups(toTree(res.data.results || [])))
      .catch(() => live && setGroups([]));
    return () => {
      live = false;
    };
  }, [clientId]);
  const { input, setInput, search, setSearch } = useSearchInput();
  const [status, setStatus] = useState("");
  const [perPage, setPerPage] = useState(10);

  // `search` follows the box once typing pauses (useSearchInput): `q` is an
  // ILIKE over two text columns with no trigram index, so not per keystroke.
  const params = useMemo(
    () => ({
      ...(scope.group ? { group: scope.group } : {}),
      ...(status ? { status } : {}),
      ...(search ? { q: search } : {}),
    }),
    [scope.group, status, search],
  );

  const { rows, count, loading, error, page, setPage, totalPages } = usePagedList(
    "/threads/",
    params,
    perPage,
  );

  const filtering = Boolean(scope.group || status || search);
  const from = count === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, count);

  return (
    <div className="ms-panel">
      <form
        className="ms-toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(input.trim());
        }}
      >
        <div className="ms-search">
          <Icon icon="tabler:search" />
          <input
            type="text"
            placeholder="Sarlavha yoki hujjat..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Qidiruv"
          />
        </div>

        <select
          className="ms-select ms-select--sm"
          value={scope.group}
          aria-label="Guruh"
          onChange={(e) => setScope({ group: e.target.value })}
        >
          <option value="">Barcha guruhlar</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {"— ".repeat(g.depth)}
              {g.name}
            </option>
          ))}
        </select>

        <select
          className="ms-select ms-select--sm"
          value={status}
          aria-label="Holati"
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Koʻrinadiganlar</option>
          <option value="hidden">Yashirilganlar</option>
          <option value="all">Hammasi</option>
        </select>

        {filtering && (
          <button
            type="button"
            className="ms-btn ms-btn--ghost"
            onClick={() => {
              setScope({ group: "" });
              setStatus("");
              setInput("");
              setSearch("");
            }}
          >
            <Icon icon="tabler:x" />
            Tozalash
          </button>
        )}

        <div className="ms-toolbar-spacer" />

        {/* Read-only grants may look but not start one. */}
        {canManage && (
          <button
            type="button"
            className="ms-btn ms-btn--primary"
            onClick={() => setCreating(true)}
          >
            <Icon icon="tabler:message-plus" />
            Yangi muhokama
          </button>
        )}
      </form>

      <NewThreadModal open={creating} clientId={clientId} onClose={() => setCreating(false)} />

      {loading ? (
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      ) : error ? (
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{error}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="ms-state">
          <Icon icon="tabler:message-off" />
          <span>
            {filtering
              ? "Hech narsa topilmadi"
              : "Hali muhokama yoʻq. U kimdir tizimingizda obyekt kartochkasini birinchi marta ochganda paydo boʻladi."}
          </span>
        </div>
      ) : (
        <>
          <div className="ms-table-wrap">
            <table className="ms-table ms-table--compact">
              <thead>
                <tr>
                  <th>Sarlavha</th>
                  <th style={{ width: 200 }}>Obyekt</th>
                  <th style={{ width: 170 }}>Guruh</th>
                  <th style={{ width: 110 }}>Xabarlar</th>
                  <th style={{ width: 130 }}>Holati</th>
                  <th style={{ width: 160 }}>Oxirgi xabar</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className={unseen(t) ? "ms-row-unseen" : undefined}>
                    <td className="ms-td-strong ms-td-clamp">
                      <Link className="ms-link" to={`/muhokamalar/${t.id}`}>
                        {t.title || t.external_object_id}
                      </Link>
                      <SeenPill row={t} />
                    </td>
                    <td>
                      <span className="ms-mono ms-ellipsis" title={t.external_object_id}>
                        {t.external_object_id}
                      </span>
                    </td>
                    <td className="ms-td-clamp">{t.group_name || "—"}</td>
                    <td className="ms-td-num">{t.message_count ?? 0}</td>
                    <td>
                      {t.is_hidden ? (
                        <span className="ms-badge ms-badge--danger">yashirilgan</span>
                      ) : (
                        <span className="ms-badge ms-badge--success">koʻrinadi</span>
                      )}
                    </td>
                    <td className="ms-td-muted" title={exactTime(t.last_message_at)}>
                      {t.last_message_at ? relativeTime(t.last_message_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ms-tablefoot">
            <span className="ms-tablefoot-count">
              Koʻrsatilmoqda <b>{from}</b>—<b>{to}</b> / jami <b>{count}</b> ta
            </span>

            <div className="ms-tablefoot-right">
              <label className="ms-perpage">
                <span>Koʻrsatish</span>
                <select
                  className="ms-select ms-select--sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>

              <Pager page={page} totalPages={totalPages} onPage={setPage} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThreadsPage;
